import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import usePageMeta from "../../hooks/usePageMeta";
import {
    getWholesalePriceBookAction,
    createWholesaleItemAction,
    updateWholesaleItemAction,
    deleteWholesaleItemAction,
    importWholesalePriceBookAction,
    clearAdminErrors,
} from "../../redux/actions/adminActions";
import {
    WHOLESALE_PRICE_BOOK_MUTATE_RESET,
    WHOLESALE_PRICE_BOOK_IMPORT_RESET,
} from "../../redux/constants/adminConstants";
import normalizeUpc from "../../utils/normalizeUpc";

const toSearchUpc = (raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) return "";
    return normalizeUpc(trimmed) || trimmed;
};

const EMPTY_FORM = {
    case_upc: "",
    carton_upc: "",
    case_quantity: "",
    carton_quantity: "",
    description: "",
};

const REQUIRED_COLS = ["case_upc", "carton_upc", "case_quantity", "carton_quantity"];
const ALL_COLS = [...REQUIRED_COLS, "description"];

const WholesalePriceBook = () => {
    const dispatch = useDispatch();
    const { items, totalPages, total, loading } = useSelector((s) => s.wholesalePriceBook);
    const {
        loading: mutating,
        success: mutSuccess,
        message: mutMessage,
        error: mutError,
    } = useSelector((s) => s.wholesalePriceBookMutate);
    const {
        loading: importing,
        success: importSuccess,
        inserted,
        skipped,
        error: importError,
    } = useSelector((s) => s.wholesalePriceBookImport);

    const [search, setSearch] = useState({ upc: "", description: "" });
    const [page, setPage] = useState(1);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [deleteTarget, setDeleteTarget] = useState(null);

    const [rawRows, setRawRows] = useState([]);
    const [colMap, setColMap] = useState({});
    const [hasHeader, setHasHeader] = useState(true);
    const [showImport, setShowImport] = useState(false);

    const load = useCallback(() => {
        const params = { page };
        if (search.upc.trim()) params.upc = toSearchUpc(search.upc);
        if (search.description.trim()) params.description = search.description.trim();
        dispatch(getWholesalePriceBookAction(params));
    }, [dispatch, page, search]);

    usePageMeta("wholesalePriceBook");

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        if (mutSuccess) {
            toast.success(mutMessage);
            dispatch({ type: WHOLESALE_PRICE_BOOK_MUTATE_RESET });
            setModal(null);
            setDeleteTarget(null);
            load();
        }
        if (mutError) {
            toast.error(mutError);
            dispatch(clearAdminErrors());
        }
    }, [mutSuccess, mutMessage, mutError, dispatch, load]);

    useEffect(() => {
        if (importSuccess) {
            toast.success(`Import complete: ${inserted} inserted, ${skipped} skipped`);
            dispatch({ type: WHOLESALE_PRICE_BOOK_IMPORT_RESET });
            setShowImport(false);
            setRawRows([]);
            setColMap({});
            load();
        }
        if (importError) {
            toast.error(importError);
            dispatch(clearAdminErrors());
        }
    }, [importSuccess, importError, inserted, skipped, dispatch, load]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        load();
    };

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setModal({ mode: "create" });
    };

    const openEdit = (item) => {
        setForm({
            case_upc: item.case_upc,
            carton_upc: item.carton_upc,
            case_quantity: String(item.case_quantity ?? ""),
            carton_quantity: String(item.carton_quantity ?? ""),
            description: item.description || "",
        });
        setModal({ mode: "edit", item });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!form.case_upc || !form.carton_upc || !form.case_quantity || !form.carton_quantity) {
            toast.error("Case UPC, Carton UPC, Case Qty, and Carton Qty are required");
            return;
        }
        const payload = {
            ...form,
            case_quantity: Number(form.case_quantity),
            carton_quantity: Number(form.carton_quantity),
        };
        if (modal.mode === "create") dispatch(createWholesaleItemAction(payload));
        else dispatch(updateWholesaleItemAction(modal.item.id, payload));
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const wb = XLSX.read(ev.target.result, { type: "binary" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            setRawRows(data);
            setColMap({});
        };
        reader.readAsBinaryString(file);
    };

    const previewRows = rawRows.length > 0 ? (hasHeader ? rawRows.slice(1, 6) : rawRows.slice(0, 5)) : [];
    const displayHeaders =
        rawRows.length > 0
            ? hasHeader
                ? rawRows[0].map(String)
                : rawRows[0].map((_, i) => `Column ${i + 1}`)
            : [];

    const buildItems = () => {
        const dataRows = hasHeader ? rawRows.slice(1) : rawRows;
        return dataRows
            .map((row) => {
                const item = {};
                Object.entries(colMap).forEach(([colIdx, field]) => {
                    item[field] = row[Number(colIdx)];
                });
                if (item.case_upc) {
                    const n = normalizeUpc(item.case_upc);
                    if (n) item.case_upc = n;
                }
                if (item.carton_upc) {
                    const n = normalizeUpc(item.carton_upc);
                    if (n) item.carton_upc = n;
                }
                return item;
            })
            .filter(
                (item) =>
                    item.case_upc &&
                    item.carton_upc &&
                    item.case_quantity !== undefined &&
                    item.case_quantity !== "" &&
                    item.carton_quantity !== undefined &&
                    item.carton_quantity !== ""
            );
    };

    const unmappedRequired = REQUIRED_COLS.filter((f) => !Object.values(colMap).includes(f));

    const handleImport = () => {
        const toImport = buildItems();
        if (!toImport.length) {
            toast.error("No valid rows to import");
            return;
        }
        dispatch(importWholesalePriceBookAction(toImport));
    };

    return (
        <div className="pl-[1.5vmax] pr-[2.5vmax] mt-[1vmax] mb-[4vmax]">
            <div className="flex justify-between items-center bg-white p-[1.2vmax] rounded-[1vmax] px-[2vmax] mb-[2vmax] flex-wrap gap-[1vmax]">
                <div>
                    <h2 className="text-[1.3vmax] font-bold">Wholesale Price Book</h2>
                    <p className="text-[0.9vmax] text-slate-400">{total} total items</p>
                </div>
                <div className="flex gap-[1vmax]">
                    <button
                        onClick={() => setShowImport((v) => !v)}
                        className="border border-[var(--color-2)] text-[var(--color-2)] text-[1vmax] py-[0.6vmax] px-[1.5vmax] rounded-[0.5vmax] flex items-center gap-[0.5vmax] hover:bg-[var(--color-2-light)]"
                    >
                        <i className="fa-solid fa-file-import" /> Bulk Import
                    </button>
                    <button
                        onClick={openCreate}
                        className="bg-[var(--color-2)] hover:opacity-90 text-white text-[1vmax] py-[0.6vmax] px-[1.5vmax] rounded-[0.5vmax] flex items-center gap-[0.5vmax]"
                    >
                        <i className="fa-solid fa-plus" /> Add Item
                    </button>
                </div>
            </div>

            <form
                onSubmit={handleSearch}
                className="bg-white shadow-1 rounded-2xl p-[1.5vmax] mb-[1.5vmax] flex gap-[1vmax] flex-wrap"
            >
                <input
                    className="border border-slate-300 rounded-xl px-[1vmax] py-[0.6vmax] text-[1vmax] outline-[var(--color-2)] flex-1 min-w-[10vmax]"
                    placeholder="Search by Case or Carton UPC..."
                    value={search.upc}
                    onChange={(e) => setSearch((s) => ({ ...s, upc: e.target.value }))}
                    onBlur={(e) => {
                        const normalized = normalizeUpc(e.target.value);
                        if (normalized) setSearch((s) => ({ ...s, upc: normalized }));
                    }}
                />
                <input
                    className="border border-slate-300 rounded-xl px-[1vmax] py-[0.6vmax] text-[1vmax] outline-[var(--color-2)] flex-1 min-w-[10vmax]"
                    placeholder="Search by description..."
                    value={search.description}
                    onChange={(e) => setSearch((s) => ({ ...s, description: e.target.value }))}
                />
                <button
                    type="submit"
                    className="bg-[var(--color-2)] text-white px-[1.5vmax] py-[0.6vmax] rounded-xl text-[1vmax]"
                >
                    Search
                </button>
                <button
                    type="button"
                    onClick={() => {
                        setSearch({ upc: "", description: "" });
                        setPage(1);
                    }}
                    className="bg-slate-100 px-[1.5vmax] py-[0.6vmax] rounded-xl text-[1vmax]"
                >
                    Clear
                </button>
            </form>

            {showImport && (
                <div className="bg-white shadow-1 rounded-2xl p-[2vmax] mb-[1.5vmax]">
                    <h3 className="font-bold text-[1.2vmax] mb-[1vmax]">Bulk Import Wholesale Price Book</h3>
                    <div className="flex items-center gap-[1.5vmax] mb-[1vmax] flex-wrap">
                        <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} className="text-[1vmax]" />
                        <label className="flex items-center gap-[0.5vmax] text-[1vmax] cursor-pointer">
                            <input type="checkbox" checked={hasHeader} onChange={() => setHasHeader((p) => !p)} />
                            File has header row
                        </label>
                    </div>

                    {rawRows.length > 0 && (
                        <>
                            {unmappedRequired.length > 0 && (
                                <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-xl p-[1vmax] mb-[1vmax] text-[1vmax]">
                                    Required columns not mapped: <strong>{unmappedRequired.join(", ")}</strong>
                                </div>
                            )}

                            <p className="text-[1vmax] font-semibold text-slate-600 mb-[0.5vmax]">Map Columns:</p>
                            <div className="overflow-x-auto">
                                <table className="w-full text-[0.95vmax] border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50">
                                            {displayHeaders.map((h, i) => (
                                                <th key={i} className="border px-[1vmax] py-[0.5vmax] text-left font-semibold">
                                                    <div>{h}</div>
                                                    <select
                                                        className="mt-[0.3vmax] border border-slate-300 rounded p-[0.3vmax] text-[0.85vmax] w-full outline-[var(--color-2)]"
                                                        value={colMap[i] || ""}
                                                        onChange={(e) => {
                                                            const val = e.target.value;
                                                            setColMap((prev) => {
                                                                const next = { ...prev };
                                                                Object.keys(next).forEach((k) => {
                                                                    if (next[k] === val) delete next[k];
                                                                });
                                                                if (val) next[i] = val;
                                                                else delete next[i];
                                                                return next;
                                                            });
                                                        }}
                                                    >
                                                        <option value="">-- Skip --</option>
                                                        {ALL_COLS.map((c) => (
                                                            <option key={c} value={c}>
                                                                {c}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {previewRows.map((row, ri) => (
                                            <tr key={ri} className="border-b hover:bg-slate-50">
                                                {row.map((cell, ci) => (
                                                    <td key={ci} className="border px-[1vmax] py-[0.4vmax] text-slate-600">
                                                        {String(cell)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center gap-[1vmax] mt-[1vmax]">
                                <button
                                    onClick={handleImport}
                                    disabled={unmappedRequired.length > 0 || importing}
                                    className="bg-[var(--color-2)] text-white px-[1.5vmax] py-[0.7vmax] rounded-xl text-[1vmax] disabled:opacity-50"
                                >
                                    {importing ? "Importing..." : `Import ${buildItems().length} Items`}
                                </button>
                                <span className="text-[0.9vmax] text-slate-400">
                                    {rawRows.length - (hasHeader ? 1 : 0)} rows total in file
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className="bg-white shadow-1 rounded-2xl overflow-hidden">
                {loading ? (
                    <p className="text-center py-[4vmax] text-slate-400">Loading...</p>
                ) : items.length === 0 ? (
                    <p className="text-center py-[4vmax] text-slate-400">No items in wholesale price book.</p>
                ) : (
                    <table className="w-full text-[1vmax]">
                        <thead className="bg-slate-50 border-b">
                            <tr>
                                {[
                                    { h: "Case UPC", align: "th-text" },
                                    { h: "Carton UPC", align: "th-text" },
                                    { h: "Case Qty", align: "th-center" },
                                    { h: "Carton Qty", align: "th-center" },
                                    { h: "Description", align: "th-text" },
                                    { h: "Actions", align: "th-center" },
                                ].map(({ h, align }) => (
                                    <th key={h} className={`${align} px-[1.5vmax] py-[1vmax] text-slate-600 font-semibold`}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item) => (
                                <tr key={item.id} className="border-b hover:bg-slate-50">
                                    <td className="px-[1.5vmax] py-[1vmax] td-num font-mono">{item.case_upc}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-num font-mono">{item.carton_upc}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">{item.case_quantity}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">{item.carton_quantity}</td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-text text-slate-500">
                                        {item.description || "—"}
                                    </td>
                                    <td className="px-[1.5vmax] py-[1vmax] td-center">
                                        <div className="flex gap-[0.8vmax] justify-center">
                                            <button
                                                onClick={() => openEdit(item)}
                                                className="text-[var(--color-2)] hover:opacity-70 text-[1.1vmax]"
                                            >
                                                <i className="fa-solid fa-pen" />
                                            </button>
                                            <button
                                                onClick={() => setDeleteTarget(item)}
                                                className="text-red-500 hover:opacity-70 text-[1.1vmax]"
                                            >
                                                <i className="fa-solid fa-trash" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {totalPages > 1 && (
                    <div className="flex justify-center gap-[0.5vmax] p-[1.5vmax]">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                            <button
                                key={p}
                                onClick={() => setPage(p)}
                                className={`w-[2.5vmax] h-[2.5vmax] rounded-full text-[1vmax] ${
                                    p === page
                                        ? "bg-[var(--color-2)] text-white"
                                        : "bg-slate-100 hover:bg-slate-200"
                                }`}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {modal && (
                <div className="fixed inset-0 bg-black/50 z-[999] flex-center">
                    <div className="bg-white rounded-2xl p-[2vmax] w-[90%] lg:w-[45%] shadow-xl">
                        <h3 className="font-bold text-[1.3vmax] mb-[1.5vmax]">
                            {modal.mode === "create" ? "Add Wholesale Item" : "Edit Wholesale Item"}
                        </h3>
                        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-[1vmax]">
                            {[
                                { field: "case_upc", label: "Case UPC", req: true },
                                { field: "carton_upc", label: "Carton UPC", req: true },
                                { field: "case_quantity", label: "Case Quantity", req: true, type: "number" },
                                { field: "carton_quantity", label: "Carton Quantity", req: true, type: "number" },
                                { field: "description", label: "Description", req: false },
                            ].map(({ field, label, req, type }) => (
                                <div
                                    key={field}
                                    className={`flex flex-col gap-[0.4vmax] ${
                                        field === "description" ? "col-span-2" : ""
                                    }`}
                                >
                                    <label className="text-[1vmax] font-semibold text-slate-600">
                                        {label} {req && <span className="text-red-500">*</span>}
                                    </label>
                                    <input
                                        type={type || "text"}
                                        min={type === "number" ? "1" : undefined}
                                        className="border border-slate-300 rounded-xl p-[0.8vmax] text-[1vmax] outline-[var(--color-2)]"
                                        value={form[field]}
                                        onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                                        onBlur={
                                            field === "case_upc" || field === "carton_upc"
                                                ? (e) => {
                                                      const normalized = normalizeUpc(e.target.value);
                                                      if (normalized) {
                                                          setForm((f) => ({ ...f, [field]: normalized }));
                                                      }
                                                  }
                                                : undefined
                                        }
                                        placeholder={label}
                                    />
                                </div>
                            ))}
                            <div className="col-span-2 flex justify-end gap-[1vmax] mt-[0.5vmax]">
                                <button
                                    type="button"
                                    onClick={() => setModal(null)}
                                    className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-slate-100 text-[1vmax]"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={mutating}
                                    className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-[var(--color-2)] text-white text-[1vmax] disabled:opacity-60"
                                >
                                    {mutating ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {deleteTarget && (
                <div className="fixed inset-0 bg-black/50 z-[999] flex-center">
                    <div className="bg-white rounded-2xl p-[2vmax] w-[90%] lg:w-[30%] shadow-xl text-center">
                        <i className="fa-solid fa-triangle-exclamation text-red-500 text-[3vmax] mb-[1vmax]" />
                        <h3 className="font-bold text-[1.2vmax] mb-[0.5vmax]">Delete Item</h3>
                        <p className="text-slate-500 text-[1vmax] mb-[1.5vmax]">
                            Delete case UPC <strong>{deleteTarget.case_upc}</strong> / carton{" "}
                            <strong>{deleteTarget.carton_upc}</strong>?
                        </p>
                        <div className="flex justify-center gap-[1vmax]">
                            <button
                                onClick={() => setDeleteTarget(null)}
                                className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-slate-100 text-[1vmax]"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => dispatch(deleteWholesaleItemAction(deleteTarget.id))}
                                disabled={mutating}
                                className="px-[1.5vmax] py-[0.6vmax] rounded-lg bg-red-500 text-white text-[1vmax] disabled:opacity-60"
                            >
                                {mutating ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WholesalePriceBook;
